import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Send, Store, Link2, CalendarDays, Clock, User, MessageCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { ZoneSelect } from "../components/ZoneSelect";

const empty = { venue_name: "", event_url: "", date: "", time: "", neighborhood: "", zone_text: "", submitter_name: "", submitter_contact: "", website: "" };
const input = "w-full pl-10 pr-4 py-3 rounded-xl bg-wyd-bg border border-white/10 text-white placeholder:text-wyd-muted/60 focus:ring-2 focus:ring-wyd-accent focus:outline-none";

const F = ({ label, hint, icon: Icon, children }) => (
  <div className="min-w-0">
    <label className="overline text-wyd-muted block mb-1.5">{label}</label>
    <div className="relative min-w-0">
      <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-wyd-muted pointer-events-none" />
      {children}
    </div>
    {hint && <p className="text-xs text-wyd-muted/80 mt-1.5">{hint}</p>}
  </div>
);

const Success = ({ onAgain }) => (
  <div data-testid="propose-success" className="mt-10 glass rounded-2xl p-10 text-center fade-up">
    <CheckCircle2 size={44} className="mx-auto text-wyd-accent" />
    <p className="font-display font-bold text-2xl mt-5">Grazie! Controlliamo l'evento e lo pubblichiamo a breve.</p>
    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
      <button data-testid="propose-again" onClick={onAgain} className="px-6 py-3 rounded-full bg-transparent border border-wyd-accent text-wyd-accent font-semibold hover:bg-wyd-accent/10 transition-colors duration-200">
        Proponi un altro evento
      </button>
      <Link to="/" data-testid="propose-home" className="px-6 py-3 rounded-full bg-wyd-accent text-wyd-bg font-semibold hover:glow-magenta transition-shadow duration-300">
        Torna agli eventi
      </Link>
    </div>
  </div>
);

export default function Propose() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSending(true);
    try {
      await api.post("/proposals/public", form);
      setDone(true); setForm(empty);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="pt-24 max-w-2xl mx-auto px-5 pb-16">
      <div className="overline text-wyd-accent mb-2">Per locali e community</div>
      <h1 className="font-display font-black text-4xl">Proponi un evento</h1>
      <p className="text-wyd-muted mt-3">Non serve registrarsi. Incolla il link dell'evento: leggiamo noi i dettagli, li controlliamo e lo pubblichiamo.</p>

      {done ? <Success onAgain={() => setDone(false)} /> : (
        <form onSubmit={submit} className="mt-8 space-y-5 glass rounded-2xl p-6 sm:p-8" data-testid="propose-form">
          <F label="Nome del locale *" icon={Store}>
            <input data-testid="propose-venue" required minLength={2} value={form.venue_name} onChange={set("venue_name")} placeholder="es. Bar Basso" className={input} />
          </F>
          <F label="Link all'evento *" icon={Link2} hint="Post Instagram, pagina Facebook, sito del locale o altro.">
            <input data-testid="propose-link" required value={form.event_url} onChange={set("event_url")} placeholder="https://instagram.com/p/..." className={input} />
          </F>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <F label="Data (facoltativa)" icon={CalendarDays}>
              <input data-testid="propose-date" type="date" value={form.date} onChange={set("date")} className={`${input} ios-datetime`} />
            </F>
            <F label="Orario (facoltativo)" icon={Clock}>
              <input data-testid="propose-time" type="time" value={form.time} onChange={set("time")} className={`${input} ios-datetime`} />
            </F>
          </div>
          <div className="min-w-0">
            <label className="overline text-wyd-muted block mb-1.5">Quartiere (facoltativo)</label>
            <ZoneSelect testid="propose" neighborhood={form.neighborhood} zoneText={form.zone_text}
              selectClass={input.replace("pl-10", "pl-4")} inputClass={input.replace("pl-10", "pl-4")}
              onNeighborhood={(v) => setForm((f) => ({ ...f, neighborhood: v }))} onZoneText={(v) => setForm((f) => ({ ...f, zone_text: v }))} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <F label="Il tuo nome *" icon={User}>
              <input data-testid="propose-name" required minLength={2} value={form.submitter_name} onChange={set("submitter_name")} className={input} />
            </F>
            <F label="Email o WhatsApp *" icon={MessageCircle} hint="Solo per contattarti su questo evento.">
              <input data-testid="propose-contact" required value={form.submitter_contact} onChange={set("submitter_contact")} placeholder="nome@email.it o +39 ..." className={input} />
            </F>
          </div>
          <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
          {error && <p data-testid="propose-error" className="text-sm text-red-300">{error}</p>}
          <p className="text-xs text-wyd-muted/80">Inviando accetti la <Link to="/privacy" className="underline">privacy policy</Link>. L'evento non viene pubblicato finché non lo approviamo.</p>
          <button data-testid="propose-submit" disabled={sending}
            className="w-full py-3.5 rounded-full bg-wyd-accent text-wyd-bg font-semibold flex items-center justify-center gap-2 hover:glow-magenta transition-shadow duration-300 disabled:opacity-60">
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} {sending ? "Invio..." : "Invia proposta"}
          </button>
        </form>
      )}
    </div>
  );
}
